import { Receipt, ReceiptItem, ReceiptItemOption, RECEIPT_ITEM_FIELDS, RECEIPT_ITEM_OPTION_FIELDS } from "~~/interface/Receipt";
import { useJobsStore } from "~~/stores/jobs";
import dayjs from "dayjs";
import _ from "lodash"

// create class receipt builder
export default class ReceiptBuilder {

    private receipt: Receipt

    // เวลาของรายการที่ถูกสั่งก่อนใคร อ่านจากตะกร้าตอน withItems()
    // ใช้ตั้ง metadata.startDate ตอน build() เท่านั้น ไม่ได้ขึ้นไปกับใบเสร็จ
    private firstItemTimestamp: string | null = null

    // create constructor
    constructor() {


        // this data will be change when build method is called
        this.receipt = {
            table: "A1",
            receiptId: "",
            storeId: "",
            bill : 'all',
            createdAt: new Date().toISOString(),
            items: [],
            live:true,
            employee: {
                name: "",
                employeeId: ""
            },
            payment: {

                grandTotal: 0,
                list: [],

                received: 0,
                change: 0,

                subTotal: 0,
                dc1: 0,
                sc: 0,
                dc2:0,
                cc: 0,
                tax: 0,
                rounding : 0,
                beforeTax : 0,
                note: ""
            },
            businessDate: new Date().toISOString(),
            metadata: {
                startDate: new Date().toISOString(),
                endDate: new Date().toISOString(),
                guests: 2,
                type: "dine_in",
                priceMode: "default",
                shift: "1",
                tags:{}
            },         
        }


    }


    // create method to build receipt
    build() {
        // เวลาที่รายการแรกถูกสั่ง = เวลาเปิดโต๊ะ เก็บไว้ตั้งแต่ตอน withItems()
        // ใบเสร็จไม่ได้เก็บ timestamp รายรายการแล้ว ค่านี้จึงต้องมาจากตะกร้าตอนนั้น
        if (this.firstItemTimestamp) {
            this.receipt.metadata.startDate = this.firstItemTimestamp
        }

        return this.receipt;
    }

    withTable(payload) {
        this.receipt.table = payload;
        return this;
    }
    withBill(payload) {
        if (!payload) {
            return this;
        }
        this.receipt.bill = payload;
        return this;
    }

    withStoreId(payload) {
        this.receipt.storeId = payload;
        return this;
    }

    // ของเดิมเป็น `...item` ทำให้ field ภายในของตะกร้ากับ jobchain ติดขึ้นไปกับใบเสร็จหมด
    // (__cartkey, key, row, table, bill, status, options ดิบ ฯลฯ) ซึ่งไม่มีใครอ่านต่อเลย
    // ทั้งใน microservices และ api-report-service — ดู xtable-project/nuxt3-pos-v3#60
    //
    // เปลี่ยนมาใช้ allowlist ที่ RECEIPT_ITEM_FIELDS แทน ต่อไปมี field ใหม่ในตะกร้า
    // จะไม่รั่วขึ้นไปเอง ต้องมาเพิ่มชื่อในลิสต์ก่อน
    withItems(payload) {

        // เรียงตามเวลาที่สั่งตั้งแต่ตรงนี้ แล้วจำเวลาของรายการแรกไว้
        // ของเดิมไปเรียงกับ metadata.startDate ตอน build() ซึ่งต้องพึ่ง timestamp
        // ที่ติดขึ้นไปกับใบเสร็จ พอย้ายมาอ่านจากตะกร้าแทน ใบเสร็จก็ไม่ต้องเก็บ
        // timestamp รายรายการอีกต่อไป ลำดับรายการบนใบเสร็จยังเหมือนเดิมทุกประการ
        const sorted = _.orderBy(payload, ['timestamp'], ['asc']);
        this.firstItemTimestamp = sorted.length > 0 ? (sorted[0]?.timestamp ?? null) : null;

        let items = sorted.map((item) => {
            const picked = _.pick(item, RECEIPT_ITEM_FIELDS) as unknown as ReceiptItem;

            // options2 พกฟิลด์ของ catalog ติดมาด้วย (min, max, formulas) ฝั่งที่อ่านจริง
            // ใช้แค่ 4 ตัวนี้ — thermal ใช้ name/qty, inventory ใช้ productId/qty/name,
            // รายงานฝั่ง report service ใช้ productId/qty/price/name
            //
            // รายการที่ไม่มี topping จะไม่มี key นี้ติดขึ้นไปเลย ไม่ใช่ [] ว่าง ๆ
            // ตะกร้าตั้ง options2 เป็น [] ให้ทุกรายการ (cartItems.ts:37) และบางเส้นทาง
            // ตั้งเป็น null ได้ด้วย (cartItems.ts:57) ซึ่ง null อันตรายกว่า เพราะฝั่งอ่าน
            // หลายที่กันด้วย `if (item.options2 !== undefined)` แล้วเรียก .forEach ต่อ
            // — ไม่มี key เลยจึงปลอดภัยที่สุด และทุกฝั่งที่อ่านรองรับอยู่แล้ว
            const options = Array.isArray(item?.options2) ? item.options2 : [];
            if (options.length > 0) {
                picked.options2 = options.map(
                    (option) => _.pick(option, RECEIPT_ITEM_OPTION_FIELDS) as unknown as ReceiptItemOption
                );
            } else {
                delete picked.options2;
            }

            return picked;
        })
        this.receipt.items = items;
        return this;
    }
    withPayment( {paymentList, received, change, summary, note, isEntertain} ) {

        // compute money
        this.receipt.payment = { 
            ...this.receipt.payment,
            ...summary
        }
        this.receipt.payment.list = paymentList;
        this.receipt.payment.received = received;
        this.receipt.payment.change = change;
        this.receipt.payment.note = note;
        this.receipt.payment.isEntertain = isEntertain || false;
        return this;
    }
    withGuests(payload = 2) {
        this.receipt.metadata.guests = payload;
        return this;
    }
    withReceiptId(payload) {
        this.receipt.receiptId = payload;
        return this;
    }
    withEmployee(payload) {
        this.receipt.employee = { 
            employeeId: payload.employeeId,
            name: payload.name
        };
        return this;
    }
    withCreatedAt(payload) {
        this.receipt.createdAt = payload;  
        return this;
    }
    withBusinessDate(payload) {
        this.receipt.businessDate = payload;
        return this;
    }
    withMetadata(payload) {
        this.receipt.metadata = {...this.receipt.metadata, ...payload};
        return this;
    }
    withNote(payload) {
        this.receipt.payment.note = payload;
        return this;
    }
    withCustom(payload) {
        
        this.receipt = { ...this.receipt, ...payload };
        return this;
    }

    withChannel(channel:object) {
        this.receipt.metadata.channel = channel;
        return this;
    }  
    withCategoriesSummary(payload) {
        this.receipt.categoriesSummary = payload;
        return this;
    }



}

// Nuxt auto-import (unimport) ลงทะเบียนชื่อคลาสนี้เป็น named export
// จึงต้อง export ชื่อออกไปด้วย ไม่งั้น build จะ resolve ไม่เจอ
// ส่วน default export ตัวเดิมยังอยู่ ผู้ที่ import แบบเดิมไม่กระทบ
export { ReceiptBuilder };
